import { defineComponent as _defineComponent } from 'vue';
import { createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createTextVNode as _createTextVNode, withCtx as _withCtx, Transition as _Transition } from "vue";
const _hoisted_1 = { class: "stm-wrap" };
const _hoisted_2 = { class: "stm-meta" };
const _hoisted_3 = { class: "stm-meta-label" };
const _hoisted_4 = {
    key: 0,
    class: "stm-meta-total"
};
const _hoisted_5 = {
    key: 1,
    class: "stm-meta-total"
};
const _hoisted_6 = { class: "stm-toggle" };
const _hoisted_7 = { class: "stm-tile-label-name" };
const _hoisted_8 = {
    key: 0,
    class: "stm-tile-label-value"
};
const _hoisted_9 = ["src"];
const _hoisted_10 = {
    key: 1,
    class: "stm-empty"
};
const _hoisted_11 = {
    key: 2,
    class: "stm-empty"
};
const _hoisted_12 = {
    key: 0,
    class: "stm-hint"
};
const _hoisted_13 = {
    key: 0,
    class: "stm-vouchers"
};
const _hoisted_14 = { class: "stm-drawer-tx" };
import { ref, computed, onMounted, useTemplateRef, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAppState } from '@/composables/useAppState';
import { useAuth } from '@/composables/useAuth';
import { useBanking } from '@/composables/useBanking';
import { useVouchers } from '@/composables/useVouchers';
import HeaderBlock from './elements/HeaderBlock.vue';
import MGToggle from './elements/MGToggle.vue';
import RetailDiscountCard from './elements/RetailDiscountCard.vue';
import SimpleDrawerPanel from './elements/SimpleDrawerPanel.vue';
import TransactionsList from './elements/TransactionsList.vue';
import VChart from 'vue-echarts';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { TreemapChart } from 'echarts/charts';
import { getSpendingAnalysis, getMonthRangeForSelection, groupTransactionsByDay, formatTimestampString, SPENDING_BREAKDOWN_PALETTE, } from '@/composables/useSpendingAnalysis';
import { selectedPeriod, selectedAccount, dateSelection, snapToSpendingCycle, } from './elements/spendingAnalysisFilterState';
import { formatResponseRange, getCategoryIconUrl } from '@/utils/helpers';
const MERCHANT_TOP_N = 10;
export default /*@__PURE__*/ _defineComponent({
    __name: 'SpendingTreemapPage',
    setup(__props) {
        use([CanvasRenderer, TreemapChart]);
        const state = useAppState();
        const router = useRouter();
        useAuth();
        const { getBankingInfo } = useBanking();
        const { getAvailableVouchers } = useVouchers();
        const summary = ref(null);
        const loading = ref(true);
        const flatSubCategories = ref(false);
        const zoomedName = ref(null);
        const selectedChild = ref(null);
        const zoomedBucket = ref(null);
        const listExpandedParent = ref(null);
        const chartKey = ref(0);
        const chartRef = useTemplateRef('chartRef');
        const chartEl = useTemplateRef('chartEl');
        const merchantDrawerOpen = ref(false);
        const selectedMerchantName = ref(null);
        const drawerTransactions = ref([]);
        function openMerchantDrawer(name) {
            if (!name)
                return;
            selectedMerchantName.value = name;
            const txs = merchantTransactions.value.get(name) ?? [];
            drawerTransactions.value = txs.length ? groupTransactionsByDay(txs) : [];
            // Defer opening to the next task so the originating click finishes
            // propagating before the overlay mounts. Otherwise the same click reaches
            // the freshly-teleported overlay and its @click.self handler closes it.
            setTimeout(() => {
                merchantDrawerOpen.value = true;
            }, 0);
        }
        function goToTransaction(t) {
            if (!t?.id)
                return;
            router.push('/TransactionDetail/' + t.id);
        }
        function isLightBg(color) {
            let r = 0, g = 0, b = 0;
            if (color?.startsWith('rgb')) {
                const m = color.match(/\d+/g);
                if (m)
                    [r, g, b] = m.map(Number);
            }
            else if (color?.startsWith('#') && color.length === 7) {
                r = parseInt(color.slice(1, 3), 16);
                g = parseInt(color.slice(3, 5), 16);
                b = parseInt(color.slice(5, 7), 16);
            }
            return (r * 299 + g * 587 + b * 114) / 1000 > 186;
        }
        const analysisRange = computed(() => {
            const monthRange = getMonthRangeForSelection(dateSelection.value, selectedPeriod.value);
            if (monthRange) {
                const [fy, fm] = monthRange.startMonth.split('-').map(Number);
                const [ty, tm] = monthRange.endMonth.split('-').map(Number);
                return {
                    fromDate: new Date(Date.UTC(fy, fm - 1, 1)),
                    toDate: new Date(Date.UTC(ty, tm, 1)),
                };
            }
            const now = new Date();
            let year = now.getUTCFullYear();
            let month = now.getUTCMonth();
            const cycleDay = state.bankInfo?.spendingCycle?.day;
            if (snapToSpendingCycle.value && cycleDay && now.getUTCDate() >= cycleDay) {
                month += 1;
                if (month > 11) {
                    month = 0;
                    year += 1;
                }
            }
            return {
                fromDate: new Date(Date.UTC(year, month, 1)),
                toDate: new Date(Date.UTC(year, month + 1, 0, 23, 59, 59)),
            };
        });
        const selectedAccountIds = computed(() => {
            const key = selectedAccount.value;
            const accounts = (state.bankInfo?.accounts || []).filter((a) => a.source === 0);
            if (!accounts.length || key === 'all')
                return undefined;
            const typeMap = { current: 'Current', credit: 'Credit', savings: 'Savings' };
            if (typeMap[key]) {
                const ids = accounts.filter((a) => a.accountType?.prettyName === typeMap[key]).map((a) => a.id);
                return ids.length ? ids : undefined;
            }
            const match = accounts.find((a) => a.id === key);
            return match ? [match.id] : undefined;
        });
        const baseTree = computed(() => {
            const parents = Array.isArray(summary.value?.parentCategories) ? summary.value.parentCategories : [];
            return parents
                .filter((p) => p.name !== 'Income' && (p.totalOut ?? 0) > 0)
                .map((p, i) => {
                const baseColor = p.categoryColor || p.color || SPENDING_BREAKDOWN_PALETTE[i % SPENDING_BREAKDOWN_PALETTE.length];
                const subMap = new Map();
                const txs = Array.isArray(p.transactions) ? p.transactions : [];
                for (const t of txs) {
                    if (t.amount >= 0)
                        continue;
                    const subName = t.subClassification ?? 'Other';
                    const row = subMap.get(subName) ?? { name: subName, value: 0, color: t.color ?? baseColor, iconPath: t.icon, merchants: new Map() };
                    row.value += -t.amount;
                    const mName = t.merchantName || 'Unknown';
                    row.merchants.set(mName, (row.merchants.get(mName) ?? 0) + -t.amount);
                    subMap.set(subName, row);
                }
                const children = [...subMap.values()]
                    .filter(c => c.value > 0)
                    .sort((a, b) => b.value - a.value)
                    .map(c => {
                    const merchants = [...c.merchants.entries()]
                        .map(([name, value]) => ({ name, value }))
                        .sort((a, b) => b.value - a.value);
                    // Split into "big enough to render" (top N AND ≥3% of the sub-cat
                    // total) vs the rest, which roll into a single "More" tile. The
                    // ratio threshold prevents top-10 entries that are still too tiny
                    // to fit a label.
                    const minValue = c.value * 0.03;
                    const top = [];
                    const tail = [];
                    merchants.forEach((m, i) => {
                        if (m.value <= 0)
                            return;
                        if (i < MERCHANT_TOP_N && m.value >= minValue)
                            top.push(m);
                        else
                            tail.push(m);
                    });
                    const merchantChildren = top.map(m => ({
                        name: m.name,
                        value: Math.round(m.value),
                        itemStyle: { color: c.color },
                    }));
                    if (tail.length) {
                        const otherValue = tail.reduce((s, m) => s + m.value, 0);
                        if (otherValue > 0) {
                            // Unique internal name so treemapRootToNode lands on the right
                            // More bucket — the visible label is overridden in the formatter.
                            merchantChildren.push({
                                name: `${c.name} · More`,
                                value: Math.round(otherValue),
                                itemStyle: { color: c.color },
                                children: tail.map(m => ({
                                    name: m.name,
                                    value: Math.round(m.value),
                                    itemStyle: { color: c.color },
                                })),
                            });
                        }
                    }
                    return {
                        name: c.name,
                        value: Math.round(c.value),
                        itemStyle: { color: c.color },
                        iconPath: c.iconPath,
                        children: merchantChildren.length ? merchantChildren : undefined,
                    };
                });
                return {
                    name: p.name,
                    value: Math.round(p.totalOut ?? 0),
                    itemStyle: { color: baseColor },
                    iconPath: p.name + '.svg',
                    children: children.length ? children : undefined,
                };
            })
                .sort((a, b) => b.value - a.value);
        });
        // Experimental flat view: drop the parent layer and surface every sub-category
        // at the top level, sorted by spend. Drill paths shorten by one (subcat →
        // merchant → More) — handled in onSliceClick / zoomBack via a synthetic path
        // shim so the rest of the logic stays unchanged.
        const treeData = computed(() => {
            if (!flatSubCategories.value)
                return baseTree.value;
            const flat = [];
            for (const p of baseTree.value) {
                for (const sub of p.children ?? [])
                    flat.push(sub);
            }
            return flat.sort((a, b) => b.value - a.value);
        });
        // Flat lookup of transactions by merchant name, sourced from the same
        // spending-analysis payload that feeds the treemap — so the drawer is always
        // consistent with what's on screen, regardless of date range.
        const merchantTransactions = computed(() => {
            const map = new Map();
            const parents = Array.isArray(summary.value?.parentCategories) ? summary.value.parentCategories : [];
            for (const p of parents) {
                if (p.name === 'Income')
                    continue;
                const txs = Array.isArray(p.transactions) ? p.transactions : [];
                for (const t of txs) {
                    if (t.amount >= 0)
                        continue;
                    const name = t.merchantName || 'Unknown';
                    // Map the spending-analysis row shape into the one TransactionsList
                    // expects (effectiveClassification + timestampString).
                    const mapped = {
                        id: t.id,
                        merchantName: t.merchantName,
                        amount: t.amount,
                        timestamp: t.timestamp,
                        timestampString: formatTimestampString(t.timestamp),
                        wellKnownMerchant: t.wellKnownMerchant,
                        customerTags: t.tags ?? t.customerTags,
                        effectiveClassification: {
                            mgClassification: t.classification,
                            mgSubClassification: t.subClassification,
                            color: t.color ?? p.color,
                            iconPath: t.icon,
                        },
                    };
                    const list = map.get(name) ?? [];
                    list.push(mapped);
                    map.set(name, list);
                }
            }
            return map;
        });
        const totalSpend = computed(() => treeData.value.reduce((sum, n) => sum + n.value, 0));
        const rangeLabel = computed(() => {
            const r = summary.value;
            if (r?.fromDate && r?.toDate)
                return formatResponseRange(r.fromDate, r.toDate);
            return '';
        });
        // When the user has drilled in, replace the date / total in the meta block
        // with the zoom path: parent on line 1, sub-category on line 2 (if drilled).
        const zoomTitle = computed(() => {
            if (flatSubCategories.value) {
                if (!selectedChild.value)
                    return null;
                const sub = treeData.value.find(p => p.name === selectedChild.value);
                if (!sub)
                    return null;
                return { parent: `${sub.name}: ${state.formatter.format(sub.value)}`, child: '' };
            }
            if (!zoomedName.value)
                return null;
            const parent = treeData.value.find(p => p.name === zoomedName.value);
            if (!parent)
                return null;
            const parentLine = `${parent.name}: ${state.formatter.format(parent.value)}`;
            if (!selectedChild.value)
                return { parent: parentLine, child: '' };
            const child = (parent.children ?? []).find((c) => c.name === selectedChild.value);
            if (!child)
                return { parent: parentLine, child: '' };
            return { parent: parentLine, child: `${child.name}: ${state.formatter.format(child.value)}` };
        });
        const chartOption = computed(() => {
            if (!treeData.value.length)
                return null;
            return {
                series: [{
                        type: 'treemap',
                        data: treeData.value,
                        leafDepth: 1,
                        roam: false,
                        nodeClick: false,
                        drillDownIcon: '',
                        left: 0,
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '100%',
                        height: '100%',
                        breadcrumb: { show: false },
                        // Labels are rendered as HTML overlays (see labelOverlays) so they
                        // can be cleared at the start of a zoom — toggling echarts' own
                        // labels via setOption resets the view root.
                        label: { show: false },
                        upperLabel: { show: false },
                        itemStyle: {
                            borderWidth: 0,
                            gapWidth: 0,
                            borderColor: 'transparent',
                        },
                        emphasis: {
                            disabled: false,
                            focus: 'none',
                            itemStyle: {
                                borderColor: '#332847',
                                borderWidth: 4,
                            },
                            label: { show: false },
                        },
                        levels: [
                            {
                                itemStyle: { borderWidth: 0, gapWidth: 0, borderColor: 'transparent' },
                            },
                            {
                                itemStyle: { borderWidth: 0, gapWidth: 0, borderColor: 'transparent' },
                            },
                            {
                                colorSaturation: [0.4, 0.75],
                                itemStyle: { borderWidth: 0, gapWidth: 0, borderColor: 'transparent' },
                            },
                            {
                                colorSaturation: [0.25, 0.6],
                                itemStyle: {
                                    borderWidth: 1,
                                    gapWidth: 1,
                                    borderColor: '#F9F7F3',
                                },
                                upperLabel: { show: false },
                            },
                            {
                                colorSaturation: [0.2, 0.55],
                                itemStyle: {
                                    borderWidth: 1,
                                    gapWidth: 1,
                                    borderColor: '#F9F7F3',
                                },
                                upperLabel: { show: false },
                                emphasis: {
                                    itemStyle: {
                                        borderWidth: 0,
                                        borderColor: 'transparent',
                                    },
                                },
                            },
                        ],
                    }],
            };
        });
        function onSliceClick(params) {
            const rawPath = Array.isArray(params?.treePathInfo) ? params.treePathInfo : [];
            // In flat mode the parent layer is gone, so paths are one element shorter.
            // Inject a synthetic blank parent at index 1 so the existing length-based
            // branches still index correctly into [parent, subcat, merchant, more].
            const path = flatSubCategories.value && rawPath.length >= 2
                ? [rawPath[0], { name: '' }, ...rawPath.slice(1)]
                : rawPath;
            const inst = chartRef.value?.chart;
            const hasChildren = Array.isArray(params?.data?.children) && params.data.children.length > 0;
            // Drop overlays/labels just before a zoom so they don't sit in stale
            // positions while the tiles animate; the chart's 'finished' event repopulates
            // them. Skip clearing on drawer-open clicks since the chart doesn't redraw.
            const clearOverlays = () => {
                iconOverlays.value = [];
                labelOverlays.value = [];
            };
            // path[0] is the synthetic root; [1] parent, [2] sub-category, [3] merchant,
            // [4] merchant inside a "More" bucket.
            if (path.length >= 5) {
                // Deepest leaf — a real merchant inside a More bucket. Open its drawer.
                openMerchantDrawer(path[4].name);
                return;
            }
            if (path.length === 4) {
                if (hasChildren && inst) {
                    // "More" bucket — zoom in to reveal its merchants.
                    clearOverlays();
                    zoomedName.value = path[1].name ?? null;
                    selectedChild.value = path[2].name ?? null;
                    zoomedBucket.value = path[3].name ?? null;
                    inst.dispatchAction({ type: 'treemapRootToNode', seriesIndex: 0, targetNode: path[3].name });
                    inst.dispatchAction({ type: 'downplay', seriesIndex: 0 });
                }
                else {
                    // Real merchant — open its drawer.
                    openMerchantDrawer(path[3].name);
                }
                return;
            }
            if (path.length === 3) {
                clearOverlays();
                zoomedName.value = path[1].name ?? null;
                selectedChild.value = path[2].name ?? null;
                if (inst) {
                    inst.dispatchAction({ type: 'treemapRootToNode', seriesIndex: 0, targetNode: path[2].name });
                }
                return;
            }
            if (path.length === 2) {
                clearOverlays();
                zoomedName.value = path[1].name ?? null;
                selectedChild.value = null;
                if (inst) {
                    inst.dispatchAction({ type: 'treemapRootToNode', seriesIndex: 0, targetNode: path[1].name });
                }
                return;
            }
            zoomedName.value = null;
            selectedChild.value = null;
        }
        function toggleFlat(value) {
            flatSubCategories.value = value;
            zoomedName.value = null;
            selectedChild.value = null;
            zoomedBucket.value = null;
            listExpandedParent.value = null;
            iconOverlays.value = [];
            labelOverlays.value = [];
            // Force a clean redraw so we don't try to animate between two different
            // top-level data shapes.
            chartKey.value += 1;
        }
        function zoomOut() {
            zoomedName.value = null;
            selectedChild.value = null;
            zoomedBucket.value = null;
            listExpandedParent.value = null;
            chartKey.value += 1;
        }
        function zoomBack() {
            const inst = chartRef.value?.chart;
            iconOverlays.value = [];
            labelOverlays.value = [];
            if (zoomedBucket.value) {
                // Inside a "More" bucket → back to merchant view (sub-cat root).
                zoomedBucket.value = null;
                if (inst && selectedChild.value) {
                    inst.dispatchAction({ type: 'treemapRootToNode', seriesIndex: 0, targetNode: selectedChild.value });
                }
                return;
            }
            if (selectedChild.value) {
                // Merchant view → back to sub-cat list (root = parent in nested mode,
                // or all sub-categories in flat mode).
                selectedChild.value = null;
                if (inst) {
                    if (zoomedName.value) {
                        inst.dispatchAction({ type: 'treemapRootToNode', seriesIndex: 0, targetNode: zoomedName.value });
                    }
                    else if (chartOption.value) {
                        inst.setOption({ ...chartOption.value, animation: false }, { notMerge: true });
                    }
                }
                return;
            }
            // Sub-cat view → back to top-level parents. Reset via setOption (no animation).
            zoomedName.value = null;
            listExpandedParent.value = null;
            if (inst && chartOption.value) {
                inst.setOption({ ...chartOption.value, animation: false }, { notMerge: true });
            }
            else {
                chartKey.value += 1;
            }
        }
        function load() {
            loading.value = true;
            const { fromDate, toDate } = analysisRange.value;
            getSpendingAnalysis(fromDate, toDate, {
                accountIds: selectedAccountIds.value,
                snapToSpendingCycle: snapToSpendingCycle.value,
            }, (result) => {
                summary.value = result ?? null;
                loading.value = false;
            });
        }
        function onBack() {
            // Unzoom one level at a time; only leave the page once we're fully zoomed out.
            if (zoomedName.value || selectedChild.value || zoomedBucket.value) {
                zoomBack();
                return;
            }
            router.replace('/SpendingAnalysis');
        }
        const voucherByMerchant = computed(() => {
            const m = new Map();
            for (const v of state.availableVouchers ?? []) {
                const name = v?.merchant?.name;
                if (name && (v.effectiveDiscountPercentage ?? 0) > 0) {
                    m.set(name.toLowerCase(), v);
                }
            }
            return m;
        });
        // Merchants visible in the current view that also have an available retail-card
        // voucher. "Visible" = drilled into a sub-category (selectedChild set); we
        // flatten any "More" bucket so its inner merchants are considered too.
        const voucherMerchantsInView = computed(() => {
            if (!selectedChild.value || voucherByMerchant.value.size === 0)
                return [];
            const out = [];
            const seen = new Set();
            const collect = (nodes) => {
                for (const n of nodes) {
                    if (n.children?.length) {
                        collect(n.children);
                        continue;
                    }
                    const key = (n.name ?? '').toLowerCase();
                    if (!key || seen.has(key))
                        continue;
                    const v = voucherByMerchant.value.get(key);
                    if (v) {
                        seen.add(key);
                        out.push({ voucher: v, merchantName: n.name, spend: n.value });
                    }
                }
            };
            if (flatSubCategories.value) {
                const sub = treeData.value.find(p => p.name === selectedChild.value);
                if (sub)
                    collect(sub.children ?? []);
            }
            else {
                for (const p of treeData.value) {
                    const sub = (p.children ?? []).find((s) => s.name === selectedChild.value);
                    if (!sub)
                        continue;
                    collect(sub.children ?? []);
                    break;
                }
            }
            return out.sort((a, b) => b.spend - a.spend);
        });
        const iconOverlays = ref([]);
        const labelOverlays = ref([]);
        // Flat lookup of {iconPath, color} by node name, sourced from treeData. Names
        // are unique across the parent + sub-cat levels we render icons on.
        const iconByName = computed(() => {
            const m = new Map();
            for (const p of treeData.value) {
                const pColor = p.itemStyle?.color ?? '';
                if (p.iconPath)
                    m.set(p.name, { iconPath: p.iconPath, color: pColor });
                for (const c of p.children ?? []) {
                    if (c.iconPath)
                        m.set(c.name, { iconPath: c.iconPath, color: c.itemStyle?.color ?? pColor });
                }
            }
            return m;
        });
        function computeIconOverlays() {
            const inst = chartRef.value?.chart;
            if (!inst) {
                iconOverlays.value = [];
                labelOverlays.value = [];
                return;
            }
            const series = inst.getModel().getSeriesByIndex(0);
            const root = series?.getViewRoot?.();
            if (!root) {
                iconOverlays.value = [];
                labelOverlays.value = [];
                return;
            }
            const icons = [];
            const labels = [];
            const visit = (node, depth) => {
                if (!node)
                    return;
                const layout = node.getLayout?.();
                if (!layout)
                    return;
                const minSide = Math.min(layout.width, layout.height);
                // Icon — only for tiles big enough to host it without crowding.
                const entry = iconByName.value.get(node.name);
                if (entry && minSide >= 56) {
                    const size = Math.min(48, Math.round(minSide * 0.35));
                    icons.push({
                        key: `${depth}-${node.name}`,
                        url: getCategoryIconUrl(entry.iconPath),
                        cx: layout.x + layout.width / 2,
                        cy: layout.y + layout.height / 2,
                        size,
                        light: isLightBg(entry.color ?? ''),
                    });
                }
                // Label — HTML overlay so it can be cleared during a zoom (echarts'
                // own labels can't be toggled mid-zoom without resetting the view).
                if (layout.width >= 32 && layout.height >= 22) {
                    const fill = node.getVisual?.('style')?.fill
                        || node.getModel?.().get?.('itemStyle.color')
                        || entry?.color
                        || '';
                    const isBucket = typeof node.name === 'string' && node.name.endsWith(' · More');
                    const value = typeof node.getValue === 'function' ? Number(node.getValue()) : 0;
                    const small = minSide < 64;
                    // Hide the value if the tile can't comfortably fit both name + value
                    // lines, OR if it's too narrow to render a formatted amount without
                    // ellipsing to something useless like "£1…".
                    const minHeightForValue = small ? 36 : 44;
                    const minWidthForValue = small ? 56 : 64;
                    const showValue = !isBucket
                        && layout.height >= minHeightForValue
                        && layout.width >= minWidthForValue;
                    labels.push({
                        key: `${depth}-${node.name}`,
                        name: isBucket ? 'More' : node.name,
                        value: showValue ? state.formatter.format(value) : '',
                        x: layout.x,
                        y: layout.y,
                        w: layout.width,
                        h: layout.height,
                        light: isLightBg(fill),
                        small,
                    });
                }
                // Only descend one level — matches leafDepth: 1, the visible leaves.
                if (depth < 1 && node.children?.length) {
                    for (const c of node.children)
                        visit(c, depth + 1);
                }
            };
            if (root.children?.length) {
                for (const c of root.children)
                    visit(c, 0);
            }
            iconOverlays.value = icons;
            labelOverlays.value = labels;
        }
        function onVoucherClick(voucher) {
            if (voucher?.id)
                router.push('/vouchers/amount/' + voucher.id);
            else
                router.push('/shopvouchers');
        }
        watch(chartRef, (inst) => {
            const chart = inst?.chart;
            if (!chart)
                return;
            chart.on('finished', computeIconOverlays);
        });
        watch(treeData, () => computeIconOverlays());
        onMounted(() => {
            getAvailableVouchers();
            if (state.bankInfo) {
                load();
            }
            else {
                getBankingInfo(() => load());
            }
        });
        watch(analysisRange, () => {
            zoomedName.value = null;
            selectedChild.value = null;
            zoomedBucket.value = null;
            listExpandedParent.value = null;
            load();
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(HeaderBlock, {
                    title: "Where your money goes",
                    logicalBack: true,
                    onLogicalBack: onBack
                }),
                _createElementVNode("div", _hoisted_1, [
                    _createElementVNode("div", _hoisted_2, [
                        _createElementVNode("div", _hoisted_3, _toDisplayString(rangeLabel.value), 1),
                        (zoomTitle.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(zoomTitle.value.child || zoomTitle.value.parent), 1))
                            : (totalSpend.value > 0)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(_unref(state).formatter.format(totalSpend.value)) + " spent", 1))
                                : _createCommentVNode("", true)
                    ]),
                    _createElementVNode("label", _hoisted_6, [
                        _cache[1] || (_cache[1] = _createElementVNode("span", { class: "stm-toggle-label" }, "Show sub-categories", -1)),
                        _createVNode(MGToggle, {
                            size: "small",
                            modelValue: flatSubCategories.value,
                            "onUpdate:modelValue": toggleFlat
                        }, null, 8, ["modelValue"])
                    ]),
                    _createElementVNode("div", {
                        class: "stm-chart",
                        ref_key: "chartEl",
                        ref: chartEl
                    }, [
                        (chartOption.value)
                            ? (_openBlock(), _createBlock(_unref(VChart), {
                                key: chartKey.value,
                                ref_key: "chartRef",
                                ref: chartRef,
                                option: chartOption.value,
                                autoresize: "",
                                class: "stm-chart-canvas",
                                onClick: onSliceClick
                            }, null, 8, ["option"]))
                            : _createCommentVNode("", true),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(labelOverlays.value, (l) => {
                            return (_openBlock(), _createElementBlock("div", {
                                key: l.key,
                                class: _normalizeClass(["stm-tile-label", { 'stm-tile-label--dark': l.light, 'stm-tile-label--small': l.small }]),
                                style: _normalizeStyle({
                                    left: l.x + 'px',
                                    top: l.y + 'px',
                                    width: l.w + 'px',
                                    height: l.h + 'px',
                                })
                            }, [
                                _createElementVNode("div", _hoisted_7, _toDisplayString(l.name), 1),
                                (l.value)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(l.value), 1))
                                    : _createCommentVNode("", true)
                            ], 6));
                        }), 128)),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(iconOverlays.value, (o) => {
                            return (_openBlock(), _createElementBlock("img", {
                                key: o.key,
                                src: o.url,
                                class: _normalizeClass(["stm-tile-icon", { 'stm-tile-icon--dark': o.light }]),
                                style: _normalizeStyle({
                                    left: (o.cx - o.size / 2) + 'px',
                                    top: (o.cy - o.size / 2) + 'px',
                                    width: o.size + 'px',
                                    height: o.size + 'px',
                                }),
                                alt: ""
                            }, null, 14, _hoisted_9));
                        }), 128)),
                        (!chartOption.value && loading.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_10, "Loading…"))
                            : (!chartOption.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_11, "No spending for this period"))
                                : _createCommentVNode("", true)
                    ], 512),
                    (chartOption.value)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_12, _cache[2] || (_cache[2] = [
                            _createTextVNode(" Tap a category to drill in. Tap "),
                            _createElementVNode("strong", null, "More", -1),
                            _createTextVNode(" to see smaller merchants. Use the back arrow above to zoom out. ")
                        ])))
                        : _createCommentVNode("", true),
                    _createVNode(_Transition, { name: "stm-vouchers-fade" }, {
                        default: _withCtx(() => [
                            (voucherMerchantsInView.value.length > 0)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(voucherMerchantsInView.value, (m) => {
                                        return (_openBlock(), _createBlock(RetailDiscountCard, {
                                            key: m.merchantName,
                                            voucher: m.voucher,
                                            merchantName: m.merchantName,
                                            onClick: ($event) => (onVoucherClick(m.voucher))
                                        }, {
                                            default: _withCtx(() => [
                                                _cache[3] || (_cache[3] = _createTextVNode(" You’ve spent ")),
                                                _createElementVNode("strong", null, _toDisplayString(_unref(state).formatter.format(m.spend)), 1),
                                                _createTextVNode(" at " + _toDisplayString(m.merchantName) + " in " + _toDisplayString(rangeLabel.value) + ". You could have saved up to ", 1),
                                                _createElementVNode("strong", null, _toDisplayString(_unref(state).formatter.format(m.spend * m.voucher.effectiveDiscountPercentage / 100)), 1),
                                                _cache[4] || (_cache[4] = _createTextVNode(" with Retail Cards. "))
                                            ]),
                                            _: 2
                                        }, 1032, ["voucher", "merchantName", "onClick"]));
                                    }), 128))
                                ]))
                                : _createCommentVNode("", true)
                        ]),
                        _: 1
                    })
                ]),
                _createVNode(SimpleDrawerPanel, {
                    modelValue: merchantDrawerOpen.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((merchantDrawerOpen).value = $event)),
                    title: selectedMerchantName.value || 'Transactions',
                    height: "90vh"
                }, {
                    default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_14, [
                            (drawerTransactions.value.length)
                                ? (_openBlock(), _createBlock(TransactionsList, {
                                    key: 0,
                                    search: false,
                                    transactions: drawerTransactions.value,
                                    formatter: _unref(state).formatter,
                                    today: _unref(state).today,
                                    yesterday: _unref(state).yesterday,
                                    categoryPills: true,
                                    onTransactionClick: goToTransaction
                                }, null, 8, ["transactions", "formatter", "today", "yesterday"]))
                                : _createCommentVNode("", true)
                        ])
                    ]),
                    _: 1
                }, 8, ["modelValue", "title"])
            ], 64));
        };
    }
});
